/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { mapUserData } from "@stoneage/App/Components/MarketoFormModal/GetFilteredUserData";
import { CustomAppState, xMarketoModalFormsActions } from "@stoneage/App/Store/Reducers";
import { getUserDataForMarketoForm } from "@stoneage/App/Store/Selectors/AccountSettingsSelectors";
import { getMarketoSettings } from "@stoneage/App/Store/Selectors/MarketoSettings";
import React, { useState } from "react";
import { FC, useEffect } from "react";
import { connect, DispatchProp } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const pageId = getCurrentPage(state).id;

    return {
        marketoSettings: getMarketoSettings(state),
        location: getLocation(state),
        modalForm: state.xMarketoModalForms,
        pageWidgets: getWidgetsByPageId(state, pageId),
        pageId,
        userData: getUserDataForMarketoForm(state),
    };
};

const enum fields {
    formIds = "formIds",
    fieldsMapping = "fieldsMapping",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.formIds]: string;
        [fields.fieldsMapping]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & DispatchProp<xMarketoModalFormsActions>;

export interface MarketoSettings {
    baseUrl: string;
    munchkinId: string;
    formIds: string[];
    pageId: string;
}

const MarketoFormInitializer: FC<Props> = ({
    marketoSettings,
    pageId,
    location,
    fields,
    modalForm,
    dispatch,
    pageWidgets,
    userData,
}) => {
    if (IS_SERVER_SIDE) {
        return null;
    }
    const { baseUrl, munchkinId, scriptUrl } = marketoSettings;
    const formIdsList = fields.formIds.split(",");
    const config = {
        baseUrl,
        munchkinId,
        formIds: formIdsList,
        pageId,
    };
    const init = (config: MarketoSettings, triggerByModal: boolean) => {
        const MktoInstance = (window as any).MktoForms2;
        /* const */

        MktoInstance.whenRendered((form: any) => {
            const formEl: HTMLFormElement = form.getFormElem()[0];
            const rando = `_${new Date().getTime()}${Math.random()}`;

            [...(formEl.querySelectorAll("label[for]") as NodeListOf<HTMLLabelElement>)]
                .map((labelEl: HTMLLabelElement) => {
                    return {
                        label: labelEl,
                        for: formEl.querySelector(`[id="${labelEl.htmlFor}"]`),
                    };
                })
                .forEach(labelDesc => {
                    if (labelDesc.for) {
                        if (!labelDesc.for.hasAttribute("data-uniquified")) {
                            labelDesc.for.id = labelDesc.for.id + rando;
                            labelDesc.for.setAttribute("data-uniquified", "true");
                        }
                        labelDesc.label.htmlFor = labelDesc.for.id;
                    }
                });
        });

        loadFormByIds(MktoInstance, config.formIds, triggerByModal);
    };

    const loadFormByIds = (MarketoInstance: any, ids: string[], modalTrigger: boolean) => {
        const MKTOFORM_ID_ATTRNAME = "data-formid";
        const UUID_ATTRNAME = "data-uuid";
        ids.forEach((formId: string) => {
            const loadForm = MarketoInstance.loadForm.bind(MarketoInstance, config.baseUrl, config.munchkinId, formId);
            const formEls = [...document.querySelectorAll(`form[${MKTOFORM_ID_ATTRNAME}="${formId}"]`)];

            const loadFormCB = (formEls: HTMLFormElement[]) => {
                const formEl: HTMLFormElement | undefined = formEls.shift();

                if (formEl === undefined) {
                    return;
                }
                formEl.id = `mktoForm_${formId}`;
                const uuid = formEl.getAttribute(UUID_ATTRNAME);

                const mappedData =
                    modalTrigger && modalForm.extraLocationData
                        ? mapUserData(
                              userData,
                              fields.fieldsMapping,
                              modalForm.extraLocationData.inShell,
                              modalForm.extraLocationData.location,
                              modalForm.extraLocationData.comingFromDealerPage,
                          )
                        : mapUserData(userData, fields.fieldsMapping);

                if (!formEl.hasAttribute("data-formloaded")) {
                    formEl.setAttribute("data-formloaded", "true");

                    dispatch({ type: "MarketoForm/StartLoading" });

                    loadForm((form: any) => {
                        formEl.id = "";

                        formEl.removeAttribute("data-formid");
                        dispatch({ type: "MarketoForm/StopLoading" });
                        dispatch({ type: "MarketoForm/SetFormLoaded" });
                        form.vals(mappedData);
                        
                        if (formEls.length) {
                            loadFormCB(formEls);
                        }
                        form.onSuccess((values: any, followUpUrl: string) => {
                            form.getFormElem().hide();
                            const formEl: HTMLFormElement = form.getFormElem()[0];
                            const wrapperId = formEl.parentElement?.getAttribute("id");
                            const formUuid = wrapperId?.split("_")[2] || "";
                            dispatch({ type: "MarketoForm/DisplayFormConfirmationMessage", uuid: formUuid, formId });
                            return false;
                        });
                    });
                }
            };

            loadFormCB(formEls as any);
        });
    };

    useEffect(() => {
        // const MkInstance = (window as any).MktoForms2;
        dispatch({ type: "MarketoForm/SetTotalForms", numberOfForms: config.formIds.length });
        setTimeout(() => {
            init(config, false);
        }, 700);
    }, []);

    if (config.formIds.length === 0 || config.baseUrl.length === 0 || config.munchkinId.length === 0) {
        return null;
    }

    return <></>;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(MarketoFormInitializer),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Marketo Form Initializer",
        fieldDefinitions: [
            {
                name: fields.formIds,
                displayName: "Marketo Form IDs",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: translate("Please enter the ids in a comma seperated format"),
                isRequired: true,
            },
            {
                name: fields.fieldsMapping,
                displayName: "Fields Mapping",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                placeholder: translate("Sample Input: opti_firstName:marketo_FirstName,opti_lastName:marketo_LastName"),
                fieldType: "General",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
